/**
 * Typed wrappers around the api auth endpoints.
 *   POST /auth/otp/send    — request a 6-digit code
 *   POST /auth/otp/verify  — verify the code, receive a Firebase custom token
 *   GET  /auth/me          — current user (requires a Firebase ID token)
 */
import { ApiError, apiFetch } from '../api-client';

export interface SendOtpRequest {
  /** Pass exactly one of these. */
  phone?: string;
  email?: string;
}

export interface SendOtpResponse {
  delivery: 'sms' | 'email';
  /**
   * Non-production servers MAY echo the OTP back for Iran-numbered
   * system_admin phones (Twilio can't deliver). The Welcome screen
   * navigates to /verify with `state.debugCode` set; VerifyOtp pre-
   * fills + auto-submits.
   */
  debugCode?: string;
}

export interface VerifyOtpRequest {
  phone?: string;
  email?: string;
  code: string;
}

export interface VerifyOtpResponse {
  customToken: string;
  isNewUser: boolean;
}

export interface MeResponse {
  current: {
    uid: string;
    email?: string;
    phoneNumber?: string;
    roles: string[];
    tenantId: string | null;
  };
  user: {
    id: string;
    email: string | null;
    phoneNumber: string | null;
    displayName: string | null;
    preferredLocale: string;
    tenantId: string | null;
    roles: string[];
    groupId: string | null;
    crewId: string | null;
    active: boolean;
    createdAt: string;
    avatarUrl: string | null;
    notificationPrefs: { statusUpdates: boolean; announcements: boolean } | null;
  } | null;
}

/**
 * Map an OTP send/verify failure to the i18n key of a DISTINCT,
 * actionable message — never the catch-all 'unexpected' for statuses
 * the api uses deliberately:
 *
 *   429 — resend/attempt throttle (existing lockout copy).
 *   409 — account provisioning conflict (e.g. a concurrent first-time
 *         sign-in still finishing) — retry in a moment, not "wrong code".
 *   503 — the delivery channel is unavailable (e.g. email OTP while the
 *         mail provider is mocked in prod) — try another sign-in method.
 *
 * `fallback` is the caller's generic key (sendFailed / unexpected).
 */
export function otpErrorKey(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'auth.errors.lockout';
    if (err.status === 409) return 'auth.errors.accountConflict';
    if (err.status === 503) return 'auth.errors.channelUnavailable';
    // A suspended account. Without this branch the api's deliberate,
    // specific 403 collapsed into the generic "Couldn't send the code.
    // Try again in a moment." — advice that cannot work, offered
    // forever, to the one person who most needs to know why.
    if (
      err.status === 403 &&
      (err.body as { error?: unknown } | null)?.error === 'ACCOUNT_DEACTIVATED'
    ) {
      return 'auth.errors.accountDeactivated';
    }
  }
  return fallback;
}

export const authApi = {
  sendOtp(req: SendOtpRequest): Promise<SendOtpResponse> {
    return apiFetch<SendOtpResponse>('/auth/otp/send', { method: 'POST', body: req });
  },

  verifyOtp(req: VerifyOtpRequest): Promise<VerifyOtpResponse> {
    return apiFetch<VerifyOtpResponse>('/auth/otp/verify', { method: 'POST', body: req });
  },

  me(): Promise<MeResponse> {
    return apiFetch<MeResponse>('/auth/me');
  },
};
